import { isAxiosError } from 'axios';

import {
  isApiErrorReason,
  validationReasonMessage,
} from '@/utils/publicationFailureLabel';

// A 403 carries no body worth showing: the authorization middleware answers
// text/plain "forbidden". Say what it actually means — the caller's own scope
// may have changed under them (#419 AC 13).
const FORBIDDEN_MESSAGE =
  'You are not allowed to do this — your permissions may have changed. Reload to see your current scope.';

// The backend's JSON error bodies, most specific first: a `{ reason }` code we can
// word ourselves, then a `{ error }` object, then a `{ message, errors }` validation
// body (422) whose per-field messages beat its generic `message`. Plain-text bodies
// (Go's http.Error) are handled by the caller, which has the status code a 403 needs.
function messageFromBody(data: unknown): string {
  if (typeof data === 'object' && data !== null) {
    // Ahead of `errors`: the prose there says the same thing, but with a UTC
    // timestamp the operator cannot read against their own clock.
    if ('reason' in data && isApiErrorReason(data.reason)) {
      const worded = validationReasonMessage(data.reason);
      if (worded) return worded;
    }
    if ('error' in data && typeof data.error === 'string') return data.error;
    if (
      'errors' in data &&
      typeof data.errors === 'object' &&
      data.errors !== null
    ) {
      return Object.values(data.errors).join(', ');
    }
    if ('message' in data && typeof data.message === 'string')
      return data.message;
  }
  return '';
}

/**
 * Best human-readable message for an API/network error. The single extractor —
 * the global mutation toast, the form root banner and every ad-hoc catch read
 * through here, so they cannot drift apart on which body shape wins.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback = 'Unknown error occurred',
): string => {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    const message = messageFromBody(data);
    if (message) return message;
    // Before the plain-text branch: a 403 body is the literal string "forbidden".
    if (error.response?.status === 403) return FORBIDDEN_MESSAGE;
    if (typeof data === 'string' && data.trim()) return data.trim();
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallback;
};
